import React from 'react'
import * as yup from 'yup'
import { Autocomplete, Chip } from '@mui/material'
import { Act } from '@/core/models/acts.model'
import TextInput from '@/components/ui/inputs/TextInput'

export const actAutocompleteOptionSchema = yup.object({
    _id: yup.string(),
    honoraire: yup.string().required(),
    id_transposition: yup.string().required(),
    libelle: yup.string().required(),
    isNew: yup.boolean(),
    inputValue: yup.string(),
})

export type ActAutocompleteOption = Partial<Act> & {
    isNew?: boolean
    inputValue?: string
}

export type ActsAutocompleteProps = {
    id: string
    name: string
    label: string
    editable: boolean
    selectedActs: ActAutocompleteOption[] | ActAutocompleteOption | null
    acts: ActAutocompleteOption[]
    inputValue?: string
    onInputChange?: (value: string) => void
    onActChange: (event: any, newValue: (string | ActAutocompleteOption)[] | ActAutocompleteOption | string | null, reason: string) => void
    canDelete?: boolean
    multiple?: boolean
}

export default function ActsAutoCompleteComponent(props: ActsAutocompleteProps) {
    const {
        id,
        name,
        label,
        editable,
        selectedActs,
        acts,
        inputValue,
        onInputChange,
        onActChange,
        canDelete = true,
        multiple = true,
    } = props

    return (
        <Autocomplete
            id={id}
            multiple={multiple}
            value={selectedActs}
            isOptionEqualToValue={(option, value) => option.libelle === value.libelle}
            options={acts}
            freeSolo
            autoHighlight
            autoComplete
            getOptionLabel={(option) => {
                // Value selected with enter, right from the input
                if (typeof option === 'string') {
                    return option
                }
                // Add "xxx" option created dynamically
                if (option.inputValue) {
                    return option.inputValue
                }
                // Regular option - show libelle, honoraire, and id_transposition
                return `${option.libelle} (${option.honoraire}€ - ${option.id_transposition})`
            }}
            filterOptions={(options, params) => {
                if (!options.length && params.inputValue !== '') {
                    return [
                        {
                            inputValue: params.inputValue,
                            isNew: true,
                            libelle: `Add "${params.inputValue}"`,
                            honoraire: '',
                            id_transposition: '',
                            _id: '',
                            createdBy: undefined,
                            establishment: undefined,
                        },
                    ]
                }
                return options
            }}
            renderOption={(props, option) => (
                <li {...props} key={option.libelle}>
                    <div>
                        <div style={{ fontWeight: 'bold' }}>{option.libelle}</div>
                        <div style={{ fontSize: '0.875em', color: 'text.secondary' }}>
                            Honorarium: {option.honoraire}€ • Transposition ID: {option.id_transposition}
                        </div>
                    </div>
                </li>
            )}
            selectOnFocus
            clearOnBlur
            handleHomeEndKeys
            onChange={onActChange}
            renderTags={(value, getTagProps) =>
                value.map((option, index) => {
                    const { onDelete, ...rest } = getTagProps({ index })
                    return (
                        <Chip
                            variant="outlined"
                            label={`${option.libelle} (${option.honoraire}€)`}
                            onDelete={
                                canDelete
                                    ? () =>
                                        onActChange(
                                            null,
                                            value.filter((v) => v !== option),
                                            'removeOption'
                                        )
                                    : undefined
                            }
                            {...rest}
                        />
                    )
                })
            }
            disabled={!editable}
            renderInput={(params) => (
                <TextInput
                    {...params}
                    inputProps={{
                        ...params.inputProps,
                    }}
                    label={label}
                    name={name}
                />
            )}
            inputValue={inputValue}
            onInputChange={(event, value, reason) => onInputChange?.(value)}
        />
    )
}
